var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isEqual } from 'lodash';
import { toast } from 'react-toastify';
import { AmexBalanceResetConfirmModal } from '@ReactComponents/Cards/CardsGatewayIssuingModal/Confirmation/AmexBalanceResetConfirmModal';
import { parseAndGetErrorMessage } from '@ReactComponents/Shared/ErrorAlert';
import { CardIssuingApiService as cardIssuingApiService } from '@ClientApi/cardIssuing';
import { CARD_ISSUING_ERRORS, INSTITUTION_TYPE } from '@Shared/constants';
import { CardType } from '@Shared/interfaces/CardsGatewayInterfaces';
import { CloseModal, ShowModal } from './modalActions';
import { refreshUsersIfMissing } from './usersActions';
export const SET_LOADING = 'SET_ISSUING_MODAL_LOADING';
export const INITIALIZE_DATA = 'INITIALIZE_ISSUING_MODAL_DATA';
export const SET_SERVER_ERROR = 'SET_ISSUING_MODAL_SERVER_ERROR';
export const RESET = 'RESET_ISSUING_MODAL';
export class SetLoading {
    constructor(loading) {
        this.loading = loading;
        this.type = SET_LOADING;
    }
}
export class InitializeData {
    constructor(parentCardOptions, issuingDetailsOriginalValues) {
        this.parentCardOptions = parentCardOptions;
        this.issuingDetailsOriginalValues = issuingDetailsOriginalValues;
        this.type = INITIALIZE_DATA;
    }
}
export class SetServerError {
    constructor(message) {
        this.message = message;
        this.type = SET_SERVER_ERROR;
    }
}
export class Reset {
    constructor() {
        this.type = RESET;
    }
}
const cardRequestAsIssuingDetails = (cardRequest) => ({
    id: cardRequest.id,
    user_id: cardRequest.user_id,
    description: cardRequest.purpose,
    default_category_id: cardRequest.default_category_id,
    expense_tags_to_linked_account: cardRequest.expense_tag_to_card_requests,
    allowance: {
        amount: cardRequest.budget,
        interval: cardRequest.frequency
    }
});
export const init = (virtualMode, parentLinkedAccountId, linkedAccountId, cardRequest) => (dispatch, getState) => __awaiter(void 0, void 0, void 0, function* () {
    dispatch(new SetLoading('body'));
    let originalValuesPromise = null;
    if (cardRequest) {
        originalValuesPromise = Promise.resolve(cardRequestAsIssuingDetails(cardRequest));
    }
    else if (linkedAccountId) {
        originalValuesPromise = cardIssuingApiService.getIssuingDetails(linkedAccountId, false, true);
    }
    const parentCardOptionsPromise = virtualMode ? cardIssuingApiService.getParentCards(parentLinkedAccountId) : null;
    const [parentCardOptionsResponse, originalValues] = yield Promise.all([parentCardOptionsPromise, originalValuesPromise]);
    // The issuing details only contains the user's email, so set the user_id too
    if (originalValues === null || originalValues === void 0 ? void 0 : originalValues.assigned_to) {
        const activeUsers = getState().users.usersActiveOrInvited;
        const matchingUser = activeUsers.find((user) => user.email === originalValues.assigned_to.email);
        originalValues.user_id = matchingUser === null || matchingUser === void 0 ? void 0 : matchingUser.id;
    }
    dispatch(new InitializeData(parentCardOptionsResponse === null || parentCardOptionsResponse === void 0 ? void 0 : parentCardOptionsResponse.results, originalValues));
});
export const reset = () => new Reset();
export const submit = (parentLinkedAccountId, values, linkedAccountId, companyId, issuingDetailsOriginalValues, capabilities, onSubmit) => (dispatch) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    const isEdit = Boolean(linkedAccountId);
    if (isEdit) {
        if (((_a = issuingDetailsOriginalValues === null || issuingDetailsOriginalValues === void 0 ? void 0 : issuingDetailsOriginalValues.parent_card) === null || _a === void 0 ? void 0 : _a.institution_type) === INSTITUTION_TYPE.AMEX_CPG) {
            yield makeAmexUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit);
        }
        else {
            yield makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit);
        }
    }
    else {
        yield makeCreateCardRequest(values, parentLinkedAccountId, capabilities, dispatch, onSubmit);
    }
});
function makeAmexUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit) {
    return __awaiter(this, void 0, void 0, function* () {
        if (values.allowanceAmount !== issuingDetailsOriginalValues.allowance.amount) {
            dispatch(new ShowModal(AmexBalanceResetConfirmModal, {
                onConfirm: () => makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit)
            }));
        }
        else {
            makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit);
        }
    });
}
function makeUpdateCardRequest(values, issuingDetailsOriginalValues, capabilities, linkedAccountId, companyId, dispatch, onSubmit) {
    return __awaiter(this, void 0, void 0, function* () {
        const body = buildUpdateRequestBody(values, issuingDetailsOriginalValues, capabilities);
        try {
            dispatch(new SetLoading('body'));
            dispatch(new SetServerError(null));
            const response = yield cardIssuingApiService.updateIssuingDetails(linkedAccountId, body);
            dispatch(new Reset());
            dispatch(new CloseModal());
            onSubmit && onSubmit(response);
            dispatch(new SetLoading(false));
        }
        catch (error) {
            const errorMsg = parseAndGetErrorMessage(error.message) || 'Something went wrong. Please check form fields and try again.';
            dispatch(new SetLoading(false));
            dispatch(new SetServerError(errorMsg));
        }
    });
}
export const resetCardsGatewayIssuingModal = () => new Reset();
function makeCreateCardRequest(values, parentLinkedAccountId, capabilities, dispatch, onSubmit) {
    var _a, _b;
    return __awaiter(this, void 0, void 0, function* () {
        const { parentCardId, assigneeUserIds, assigneeUserId, assigneeEmail, cardName, allowanceDailyLimit, allowanceMerchantCategories, allowanceMerchantGroupCategories, allowanceVendor, allowanceAmount, allowanceInterval, allowanceStartDate, allowanceEndDate, allowanceTransactionLimit, cardType, addressRecipient, addressStreet, addressCity, addressState, addressZipCode, addressPhoneNumber, selectedCategoryId, selectedExpenseTagsByBucketId, textExpenseTagsByBucketId, cardRequestId, cardRequestVersion, firstName, lastName, shippingService, shippingRequireSignature, cardTemplateId } = values;
        const expenseTagIds = Object.values(selectedExpenseTagsByBucketId);
        const multipleCards = (assigneeUserIds === null || assigneeUserIds === void 0 ? void 0 : assigneeUserIds.length) > 0;
        let body = {
            assignee_user_ids: multipleCards ? assigneeUserIds : null,
            assignee_user_id: multipleCards ? null : assigneeUserId,
            assignee_email: multipleCards ? null : assigneeEmail,
            parent_linked_account_id: parentLinkedAccountId,
            card_name: cardName,
            is_ghost: cardType === CardType.Ghost,
            is_virtual: cardType === CardType.Virtual,
            allowance_daily_limit: allowanceDailyLimit,
            default_category_id: selectedCategoryId,
            expense_tag_ids: expenseTagIds,
            text_expense_tags: textExpenseTagsByBucketId,
            card_request_id: cardRequestId,
            card_request_version: cardRequestVersion,
            first_name: firstName,
            last_name: lastName,
            shipping_service: shippingService,
            shipping_require_signature: shippingRequireSignature,
            card_template_id: cardTemplateId
        };
        if ((_a = capabilities === null || capabilities === void 0 ? void 0 : capabilities.allowance) === null || _a === void 0 ? void 0 : _a.transactionLimit) {
            body.allowance_transaction_limit = allowanceTransactionLimit;
        }
        if (cardType === CardType.Virtual) {
            body.parent_card_id = parentCardId;
        }
        else if (cardType === CardType.Physical) {
            body = Object.assign(Object.assign({}, body), { address_recipient: addressRecipient, address_street: addressStreet, address_city: addressCity, address_state: addressState, address_zip_code: addressZipCode, address_phone_number: addressPhoneNumber });
        }
        if (capabilities.allowance.startDate) {
            body.start_date = allowanceStartDate === null || allowanceStartDate === void 0 ? void 0 : allowanceStartDate.format('YYYY-MM-DD');
        }
        if (capabilities.allowance.endDate) {
            body.end_date = allowanceEndDate === null || allowanceEndDate === void 0 ? void 0 : allowanceEndDate.format('YYYY-MM-DD');
        }
        if (capabilities.allowance.intervalAmount) {
            body.allowance_amount = allowanceAmount;
            body.allowance_interval = allowanceInterval;
        }
        if (capabilities.allowance.supportedMerchantRestrictions > 0) {
            body.allowance_merchant_categories = allowanceMerchantCategories;
        }
        if (capabilities.allowance.supportedMerchantGroupRestrictions > 0) {
            body.allowance_merchant_group_categories = allowanceMerchantGroupCategories;
        }
        if ((_b = capabilities.allowance) === null || _b === void 0 ? void 0 : _b.allowedVendor) {
            body.allowance_vendor = allowanceVendor;
        }
        try {
            dispatch(new SetLoading('body'));
            dispatch(new SetServerError(null));
            const response = yield cardIssuingApiService.post(body);
            dispatch(new Reset());
            dispatch(new CloseModal());
            onSubmit && onSubmit(response);
            let containsInvite;
            if (Array.isArray(response)) {
                containsInvite = Boolean(response.find((res) => res.status && res.status === 'pending_invite'));
            }
            else {
                containsInvite = 'status' in response && response.status === 'pending_invite';
            }
            if (window.FEATURES.accelerated_card_issuing && containsInvite) {
                toast.success('Invite sent successfully.  Card will be issued once user accepts invite.', { autoClose: false });
                dispatch(refreshUsersIfMissing(response.user_id));
            }
            else {
                toast.success('Card Created!');
            }
        }
        catch (error) {
            const errorMessage = getMessageFromError(error);
            dispatch(new SetLoading(false));
            dispatch(new SetServerError(errorMessage));
        }
    });
}
/**
 * This function will only update a subset of the form values. If adding new fields,
 * this list should be updated.
 */
function buildUpdateRequestBody(values, issuingDetailsOriginalValues, capabilities) {
    var _a;
    const { cardName, allowanceAmount, allowanceInterval, allowanceStartDate, allowanceEndDate, allowanceMerchantCategories, allowanceMerchantGroupCategories, allowanceVendor, allowanceTransactionLimit } = values;
    const body = {};
    if (window.FEATURES.emburse_cards_gateway_issuing_expense_fields) {
        const expenseTagIds = Object.values(values.selectedExpenseTagsByBucketId);
        // Something annoying is that undefined values will not be included in the request payload,
        // so need to send a different falsy value that is valid JSON.
        body.default_category_id = values.selectedCategoryId || false;
        body.expense_tag_ids = expenseTagIds;
        body.text_expense_tags = values.textExpenseTagsByBucketId;
    }
    const allowanceStartDateString = allowanceStartDate && allowanceStartDate.format('YYYY-MM-DD');
    const allowanceEndDateString = allowanceEndDate && allowanceEndDate.format('YYYY-MM-DD');
    const allowanceUpdated = allowanceAmount !== issuingDetailsOriginalValues.allowance.amount ||
        allowanceInterval !== issuingDetailsOriginalValues.allowance.interval ||
        allowanceStartDateString !== issuingDetailsOriginalValues.allowance.start_time ||
        allowanceEndDateString !== issuingDetailsOriginalValues.allowance.end_time ||
        allowanceTransactionLimit !== issuingDetailsOriginalValues.allowance.transaction_limit ||
        !isEqual(allowanceMerchantCategories, issuingDetailsOriginalValues.allowance.scope) ||
        !isEqual(allowanceMerchantGroupCategories, issuingDetailsOriginalValues.allowance.group_scope) ||
        !isEqual(allowanceVendor, issuingDetailsOriginalValues.allowance.vendor);
    if (cardName !== issuingDetailsOriginalValues.cardName) {
        body.card_name = cardName;
    }
    if (allowanceUpdated) {
        if (capabilities.allowance.startDate) {
            body.start_date = allowanceStartDateString;
        }
        if (capabilities.allowance.endDate) {
            body.end_date = allowanceEndDateString;
        }
        if (capabilities.allowance.intervalAmount) {
            body.allowance_amount = allowanceAmount;
            body.allowance_interval = allowanceInterval;
        }
        if (capabilities.allowance.supportedMerchantRestrictions > 0) {
            body.allowance_merchant_categories = allowanceMerchantCategories;
        }
        if (capabilities.allowance.supportedMerchantGroupRestrictions > 0) {
            body.allowance_merchant_group_categories = allowanceMerchantGroupCategories;
        }
        if ((_a = capabilities.allowance) === null || _a === void 0 ? void 0 : _a.allowedVendor) {
            body.allowance_vendor = allowanceVendor;
        }
        if (capabilities.allowance.transactionLimit) {
            body.allowance_transaction_limit = allowanceTransactionLimit;
        }
    }
    return body;
}
function getMessageFromError(error) {
    const message = error.message;
    if (message.includes(CARD_ISSUING_ERRORS.EXISTING_PHYSICAL_CARD)) {
        return 'Users cannot be issued more than 1 physical card from the same card program.';
    }
    else if (message.includes(CARD_ISSUING_ERRORS.NO_PHONE_NUMBER)) {
        return "Employee doesn't have a phone number - card cannot be issued";
    }
    else if (message.includes(CARD_ISSUING_ERRORS.CARDHOLDER_STATUS_DEACTIVATED)) {
        return 'The card cannot be issued as this user’s profile doesn’t meet the American Express risk criteria and was declined. Please contact us if you need further assistance.';
    }
    else if (message.includes(CARD_ISSUING_ERRORS.CARDHOLDER_STATUS_PENDING)) {
        return 'The card cannot be issued as this user’s profile is undergoing compliance review. Please contact us if you need further assistance.';
    }
    else if (message.includes(CARD_ISSUING_ERRORS.INVALID_USER_ADDRESS)) {
        return 'You cannot issue a card to this user as it doesn’t have a US valid address';
    }
    return parseAndGetErrorMessage(error === null || error === void 0 ? void 0 : error.message) || 'Something went wrong. Please check form fields and try again.';
}
